import { useState } from "react";
import API from "@/lib/api";
import { toast } from "sonner";

/**
 * Streaming CSV export with live progress card.
 *
 * Why fetch() instead of axios responseType=blob: axios buffers the
 * whole response before resolving — on 30k-brand portfolios that's
 * ~3-4MB and a frozen UI for 6+ seconds. fetch() exposes the body
 * ReadableStream so we can tick a per-chunk byte counter and an
 * elapsed-ms timer while the bytes land. UX feels alive even though
 * the server can't send a Content-Length (the response is a true
 * StreamingResponse with no buffered total).
 *
 * Phases: idle → preparing → streaming → done → idle (auto-clears
 * 4s after completion so the card doesn't linger forever).
 */
export default function useBrandsCsvExport() {
  const [exportState, setExportState] = useState({
    phase: "idle",
    received: 0,
    total: 0,
    elapsedMs: 0,
    startedAt: 0,
  });

  const downloadCsv = async (filters = {}) => {
    if (exportState.phase === "preparing" || exportState.phase === "streaming") return;
    const startedAt = Date.now();
    setExportState({ phase: "preparing", received: 0, total: 0, elapsedMs: 0, startedAt });

    const ticker = setInterval(() => {
      setExportState((s) => (
        s.phase === "preparing" || s.phase === "streaming"
          ? { ...s, elapsedMs: Date.now() - s.startedAt }
          : s
      ));
    }, 250);

    try {
      const params = new URLSearchParams();
      if (filters.network && filters.network !== "all") params.set("network", filters.network);
      if (filters.country && filters.country !== "all") params.set("country", filters.country);
      if (filters.q && filters.q.trim()) params.set("q", filters.q.trim());

      const baseURL = API.defaults?.baseURL || "";
      const token = localStorage.getItem("token") || "";
      const qs = params.toString();
      const resp = await fetch(`${baseURL}/publisher/brands/export.csv${qs ? `?${qs}` : ""}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!resp.ok) {
        throw new Error(`HTTP ${resp.status}`);
      }
      const total = parseInt(resp.headers.get("X-Total-Count") || "0", 10);
      setExportState((s) => ({ ...s, phase: "streaming", total }));

      const reader = resp.body.getReader();
      const chunks = [];
      let received = 0;
      // eslint-disable-next-line no-constant-condition
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        chunks.push(value);
        received += value.length;
        setExportState((s) => ({ ...s, received }));
      }

      const blob = new Blob(chunks, { type: "text/csv" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const disp = resp.headers.get("Content-Disposition") || "";
      const m = disp.match(/filename="([^"]+)"/);
      a.download = m ? m[1] : `my-brands-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);

      setExportState((s) => ({ ...s, phase: "done", elapsedMs: Date.now() - s.startedAt }));
      toast.success(`Exported ${total.toLocaleString()} brand${total === 1 ? "" : "s"} to CSV`);
    } catch (err) {
      console.error("CSV export failed", err);
      toast.error("CSV export failed. Please try again.");
      setExportState({ phase: "idle", received: 0, total: 0, elapsedMs: 0, startedAt: 0 });
    } finally {
      clearInterval(ticker);
      setTimeout(() => setExportState((s) => (
        s.phase === "done" ? { phase: "idle", received: 0, total: 0, elapsedMs: 0, startedAt: 0 } : s
      )), 4000);
    }
  };

  const isExporting = exportState.phase === "preparing" || exportState.phase === "streaming";

  return { exportState, downloadCsv, isExporting };
}
